"use client";

//Неповторимый оригинал https://github.com/mattrothenberg/react-overflow-list/tree/main
import { useMount, useMeasure, usePrevious, useShallowCompareEffect, useUpdateEffect } from 'react-use';
import { ReactNode, useEffect, useRef, useState } from "react";
type CollapseDirection = 'start' | 'end';
type OverflowDirection = 'none' | 'grow' | 'shrink';
type Mode = 'horizontal' | 'vertical';
export interface OverflowListProps<T> {
  items: T[];
  itemRenderer: (item: T, index: number) => ReactNode;
  overflowRenderer: (items: T[]) => ReactNode;
  minVisibleItems?: number;
  minHeight?: number;
  onOverflow?: (items: T[]) => void;
  hasOverflowItems?: (isOverflow: boolean) => void;
  collapseFrom?: CollapseDirection;
  mode: Mode;
  gap: number;
  className?: string;
  tagName?: keyof JSX.IntrinsicElements;
  alwaysRenderOverflow?: boolean;
}
interface OverflowListState<T> {
  visible: T[];
  overflow: T[];
  lastOverflowCount: number;
  overflowDirection: OverflowDirection;
}
export function OverflowList<T>(props: OverflowListProps<T>) {
  const {
    items,
    collapseFrom = 'end',
    mode = 'horizontal',
    minVisibleItems = 0,
    minHeight = 148,
    tagName = 'div',
    gap = 4,
    className = '',
    alwaysRenderOverflow = false,
    overflowRenderer,
    itemRenderer,
    hasOverflowItems
  } = props;
  const [state, setState] = useState<OverflowListState<T>>({
    visible: items,
    overflow: [],
    lastOverflowCount: 0,
    overflowDirection: 'none'
  });
  const spacer = useRef<HTMLDivElement>(null);
  useShallowCompareEffect(() => {
    repartition(false);
  }, [state]);
  useMount(() => {
    repartition(false);
  });
  useUpdateEffect(() => {
    setState(() => ({
      overflowDirection: 'none',
      lastOverflowCount: 0,
      overflow: [],
      visible: items
    }));
  }, [items]);
  const WrapperComponent = tagName;
  const maybeOverflow = state.overflow.length === 0 && !alwaysRenderOverflow ? null : overflowRenderer(state.overflow);
  const repartition = (growing: boolean) => {
    if (!spacer.current) {
      return;
    }
    const modeDetect = mode === 'horizontal' ? 'width' : 'height';
    if (growing) {
      setState(state => ({
        overflowDirection: 'grow',
        lastOverflowCount: state.overflowDirection === 'none' ? state.overflow.length : state.lastOverflowCount,
        overflow: [],
        visible: props.items
      }));
    } else if (spacer.current.getBoundingClientRect()[modeDetect] < 0.9) {
      setState(state => {
        if (state.visible.length <= minVisibleItems!) {
          return state;
        }
        const collapseFromStart = collapseFrom === 'start';
        const visible = state.visible.slice();
        const next = collapseFromStart ? visible.shift() : visible.pop();
        if (!next) {
          return state;
        }
        const overflow = collapseFromStart ? [...state.overflow, next] : [next, ...state.overflow];
        return {
          ...state,
          direction: state.overflowDirection === 'none' ? 'shrink' : state.overflowDirection,
          overflow,
          visible
        };
      });
    } else {
      setState(prevState => {
        return {
          ...prevState,
          overflowDirection: 'none'
        };
      });
    }
  };
  const [ref, {
    width
  }] = useMeasure<any>();
  const previousWidth = usePrevious(width);
  useEffect(() => {
    if (!previousWidth) return;
    repartition(width > previousWidth);
  }, [width, previousWidth]);
  useEffect(() => {
    if (!previousWidth) return;
    if (hasOverflowItems) {
      hasOverflowItems(Boolean(state.overflow.length));
    }
  }, [previousWidth]);
  return <>
            <WrapperComponent
    // @ts-ignore
    ref={ref} className={className} style={mode === 'horizontal' ? {
      display: 'flex',
      flexWrap: 'nowrap',
      minWidth: 0
    } : {
      display: 'flex',
      flexWrap: 'wrap',
      minHeight: minHeight,
      minWidth: 0
    }} data-sentry-element="WrapperComponent" data-sentry-source-file="original.tsx">
                <div style={mode === 'horizontal' ? {
        display: 'flex',
        flexWrap: 'nowrap',
        gap: gap
      } : {
        display: 'flex',
        flexWrap: 'wrap',
        alignContent: 'flex-start',
        gap: gap
      }}>
                    {collapseFrom === 'start' ? maybeOverflow : null}
                    {state.visible.map(itemRenderer)}
                    {collapseFrom === 'end' ? maybeOverflow : null}
                </div>
                <div style={mode === 'horizontal' ? {
        flexShrink: 1,
        width: 1
      } : {
        flexShrink: 1,
        width: '100%'
      }} ref={spacer} />
            </WrapperComponent>
        </>;
}